import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, Mail, Phone, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const ProfileSettings = ({ user, onUpdate }) => {
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!name || !email) {
      toast.error('Ad ve email zorunludur');
      return;
    }

    setSaving(true);

    try {
      const response = await axios.put(`${API}/user/profile`, {
        name,
        email,
        phone
      }, { withCredentials: true });

      toast.success('Profil bilgileri güncellendi');
      
      // Update parent component
      if (onUpdate) {
        onUpdate(response.data.user);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Profil güncellenemedi');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="w-5 h-5 text-emerald-600" />
          Profil Bilgileri
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="profile-name">Ad Soyad</Label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="profile-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="profile-email">Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="profile-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Email değiştirirseniz yeni adrese doğrulama gerekebilir
            </p>
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="profile-phone">Telefon Numarası</Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="profile-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="pl-10"
                placeholder="05XX XXX XX XX"
              />
            </div>
          </div>

          {/* User ID - Read Only */}
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Kullanıcı ID (Değiştirilemez)</p>
            <p className="text-sm font-mono text-slate-700">{user?.user_id}</p>
          </div>

          {/* Role - Read Only */}
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Hesap Türü</p>
            <p className="text-sm font-medium text-slate-700">
              {user?.role === 'admin' ? '👑 Admin' : user?.role === 'operator' ? '🏢 İşletmeci' : '👤 Müşteri'}
            </p>
          </div>

          <Button 
            type="submit" 
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ProfileSettings;
